import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { Logo } from "@/components/brand";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import MobileTabBar from "@/components/MobileTabBar";
import { cn } from "@/lib/utils";

export default function DashboardShell({ roleTag, navItems }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/"); };

  const nav = (
    <nav className="flex flex-col gap-0.5" data-testid="dashboard-nav">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setMobileOpen(false)}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2.5 text-sm transition-colors",
              isActive ? "bg-[#0a0a0a] text-white" : "text-gray-600 hover:bg-gray-100 hover:text-[#0a0a0a]"
            )
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-white flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-gray-200 h-screen sticky top-0">
        <div className="h-16 flex items-center px-6 border-b border-gray-200">
          <Logo />
        </div>
        <div className="px-4 pt-5 pb-3">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400">{roleTag}</span>
        </div>
        <div className="px-3 flex-1 overflow-auto">{nav}</div>
        <div className="border-t border-gray-200 p-4">
          <div className="mb-3">
            <div className="text-sm font-semibold text-[#0a0a0a] truncate">{user?.company_name || user?.name}</div>
            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-600 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 lg:h-16 border-b border-gray-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 bg-white/95 backdrop-blur z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden -ml-1 p-1" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
            <div className="lg:hidden"><Logo /></div>
            <span className="hidden sm:inline font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">{roleTag}</span>
          </div>
          <NotificationBell />
        </header>
        <main
          className="flex-1 p-4 lg:p-8 max-w-[1400px] w-full lg:pb-8"
          style={{ paddingBottom: "calc(72px + env(safe-area-inset-bottom))" }}
        >
          <Outlet />
        </main>
      </div>

      {/* Mobile drawer (full nav w/ sign-out) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-white flex flex-col">
            <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
              <Logo />
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-4 py-3">
              <div className="text-sm font-semibold text-[#0a0a0a] truncate">{user?.company_name || user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            </div>
            <div className="px-3 flex-1 overflow-auto">{nav}</div>
            <div className="border-t border-gray-200 p-4">
              <button onClick={doLogout} data-testid="mobile-logout-button" className="flex items-center gap-2 text-sm text-gray-600">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom tab bar for phones */}
      <MobileTabBar navItems={navItems} />
    </div>
  );
}

/* Page header used inside dashboards */
export function PageHeader({ index, eyebrow, title, tag, actions }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-4 mb-4">
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
          [{index}] {eyebrow}
        </span>
        <span className="h-px flex-1 bg-gray-200" />
        {tag && <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">{tag}</span>}
      </div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#0a0a0a]">{title}</h1>
        {actions}
      </div>
    </div>
  );
}
